import { useEffect, useState } from "react";
import {
  meetKind,
  meetKindShown,
  meetUrlSay,
  safeMeetUrl,
  type MeetKind,
} from "@greg-chick/shared";
import { getNotices, saveNotices, checkMeetUrl, type NoticeDoc } from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { Msg, Empty } from "./ui";
import { MeetPicker } from "./MeetPicker";
import { isoFromKstDate, kstDateInput, kstTodayIso } from "@greg-chick/shared"; // 🕘 한국 시각 — 기기 시간대와 상관없이(kstTime.ts · 2026-10-16)

/**
 * 마을 소식 편집(FR-COM) — 운영자가 쓴 글이 웹 "커뮤니티" 화면에 그대로 보인다.
 * 사용자 글쓰기(UGC)는 정책상 없다(아동안전·무수집) — 여기가 유일한 발행 창구.
 * 저장은 목록 전체 교체(간단·예측 가능). id 는 제목에서 자동 생성하되 중복은 숫자 접미.
 */
export function NoticePanel() {
  const [list, setList] = useState<NoticeDoc[]>([]);
  const [contactUrl, setContactUrl] = useState("");
  /**
   * 🏠 **기본 회의 주소** — 행사·수업에 따로 안 넣으면 여기로 열린다.
   *
   * 🔴 왜 이 화면인가: 저장되는 곳이 **이 화면이 가진 문서**(소식 문서)라서다.
   * 다른 화면에서 저장하게 하면 그 화면이 모르는 **소식을 통째로 덮어쓴다** —
   * 이 저장소가 책·굿즈에서 이미 당한 사고다([[published-vs-repo-lists]]).
   */
  const [defaultMeetUrl, setDefaultMeetUrl] = useState("");
  /**
   * 🎥 **어느 서비스를 보고 있나.** 🔴 효과로 맞추지 않는다 — 값이 바뀔 때 같이 돌면서
   * 방금 고른 것을 되돌린다(선생님 화면이 「저장했어요」를 스스로 지웠던 그 모양).
   * 붙여넣은 주소가 아는 서비스면 그것이 이기고, 아니면 운영자가 고른 것.
   */
  const [pickedKind, setPickedKind] = useState<MeetKind | null>(null);
  // 🔴 판정은 shared 한 곳(`meetKindShown`) — 화면마다 짜서 갈렸던 자리다
  const meetPickKind = meetKindShown(defaultMeetUrl, pickedKind);
  /** 🔎 서버가 그 주소를 열어 본 결과. `null` 은 **못 물어봤다**(「없다」와 다르다). */
  const [meetSay, setMeetSay] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getNotices()
      .then((r) => {
        setList(r.notices);
        setContactUrl(r.contactUrl);
        setDefaultMeetUrl(r.defaultMeetUrl ?? "");
        setLoaded(true);
      })
      .catch(() => setMsg("엣지 서버에 연결하지 못했어요. 서버가 떠 있는지 확인해 주세요."));
  }, []);

  function update(i: number, patch: Partial<NoticeDoc>) {
    setList((prev) => prev.map((n, idx) => (idx === i ? { ...n, ...patch } : n)));
  }
  function add() {
    const iso = kstTodayIso(Date.now()); // 오늘(한국) 아침 9시 — 기기 날짜가 아니다
    setList((prev) => [
      { id: nextId(prev, "notice"), title: "", body: "", date: iso, pinned: false },
      ...prev,
    ]);
  }
  function remove(i: number) {
    setList((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function onSave() {
    /**
     * 🔴 **못 읽었으면 저장하지 않는다.**
     *
     * 이 문서에는 소식뿐 아니라 **운영자 설정**이 함께 있다(문의 폼 주소·기본 회의 주소).
     * 저장은 **전체 교체**라, 못 읽은 상태(빈 칸)에서 저장하면 그 설정이 통째로 지워진다.
     * 운영자의 첫 규칙이 「셋팅값은 초기화 하지마」다.
     *
     * 엣지도 빈 문서로 덮는 것을 거절하지만(409), 화면이 **자기가 못 읽은 걸 알고 있다** —
     * 그러면 서버에 물어볼 것도 없이 여기서 멈추는 게 맞다. 던지면 저장 바가
     * `saveFailSay` 로 이 말을 그대로 보여 준다.
     */
    if (!loaded) {
      throw new Error(
        "아직 소식·설정을 못 읽었어요. 이대로 저장하면 문의 폼 주소와 기본 화상 방 주소가 지워질 수 있어 저장하지 않았습니다 — 새로 열어 주세요.",
      );
    }
    // 제목·내용이 빈 항목은 저장 전에 걸러준다(서버도 버리지만, 운영자에게 결과를 정확히 알려주려고)
    const clean = list.filter((n) => n.title.trim() && n.body.trim());
    const dropped = list.length - clean.length;
    const count = await saveNotices(
      clean,
      contactUrl.trim() || undefined,
      defaultMeetUrl.trim() || undefined,
    );
    setList(clean);
    /**
     * 🔴 **버려진 것을 말하지 않으면 「저장했는데 비어 있다」가 된다.** 서버는 못 쓰는
     * 회의 주소를 버리는데(아이 화면이 눌러 새 창을 여는 값이라 마땅하다), 화면이
     * 「✅ 올렸어요」라고만 하면 운영자는 넣었다고 믿는다. 그리고 행사 당일에 안다.
     *
     * 판정은 서버가 쓰는 것과 **같은 함수**(`safeMeetUrl`)여야 한다 — 갈리면
     * 「화면은 괜찮다는데 서버는 버리는」 값이 생긴다.
     */
    const typed = defaultMeetUrl.trim();
    const meetDropped = typed !== "" && safeMeetUrl(typed) === null;
    setMsg(
      `✅ 소식 ${count}개를 올렸어요. 웹 [커뮤니티] 화면에 바로 보여요.` +
        (dropped > 0 ? ` (제목·내용이 빈 ${dropped}개는 저장하지 않았어요)` : "") +
        (meetDropped
          ? " ⚠️ 다만 기본 화상 방 주소는 저장하지 않았어요 — https 로 시작하는 회의 링크여야 해요."
          : ""),
    );
  }
  useRegisterSave({
    id: "notice",
    label: "마을 소식",
    save: onSave,
    draft: {
      data: () => ({ list, contactUrl, defaultMeetUrl }),
      restore: (d) => {
        const v = d as { list?: NoticeDoc[]; contactUrl?: string; defaultMeetUrl?: string };
        if (v.list) setList(v.list);
        if (typeof v.contactUrl === "string") setContactUrl(v.contactUrl);
        if (typeof v.defaultMeetUrl === "string") setDefaultMeetUrl(v.defaultMeetUrl);
      },
    },
  });

  return (
    <section className="panel notice-panel">
      <p className="hint">
        여기에 쓴 글이 웹 <b>커뮤니티</b> 화면에 그대로 보여요. 아이들이 보는 곳이라
        댓글·사용자 글쓰기는 없어요(운영자 발행만).
      </p>

      <label className="notice-panel__contact">
        🏠 기본 화상 방 주소(https)
        <input
          type="url"
          value={defaultMeetUrl}
          onChange={(e) => {
            setDefaultMeetUrl(e.target.value);
            // 🔴 다시 치면 고른 것을 비운다 — 붙여넣은 주소가 갈래를 다시 정하게
            setPickedKind(null);
            // 🔴 방금 확인한 답은 **다른 주소에 대한 답**이 된다 — 지운다
            setMeetSay(null);
          }}
          placeholder={meetKind(meetPickKind).placeholder}
        />
        <small>
          방을 <b>한 번만 만들어</b> 여기 넣어 두세요. 행사·수업에 따로 안 넣으면 모두 이
          방으로 열려요 — 매번 새 방을 만들 필요가 없어요. (다른 방을 쓰고 싶은 행사만 거기에
          따로 넣으면 그쪽이 이겨요.)
        </small>
      </label>
      {/**
        * 🔎 **여기에도 확인 단추가 있어야 한다.** 이 방은 **모든** 행사·수업이 쓰는데,
        * 확인할 길이 있는 곳은 행사 편집기와 선생님 화면뿐이었다 — 제일 중요한 자리만
        * 못 재고 있었던 것이다. 판정은 같은 것(`meetVerdict`)을 쓴다.
        *
        * 🔴 못 물어봤으면 **아무 말도 안 한다** — 「없다」로 단정하면 멀쩡한 주소를 고치게 된다.
        */}
      <div className="notice-panel__meetrow">
        <button
          className="btn"
          disabled={!defaultMeetUrl.trim() || checking}
          onClick={async () => {
            setChecking(true);
            const v = await checkMeetUrl(defaultMeetUrl.trim());
            setChecking(false);
            if (v) setMeetSay(v.say);
          }}
        >
          {checking ? "확인 중…" : "🔎 주소 확인"}
        </button>
      </div>
      {/**
        * 🔴 **조용히 사라지는 자리였다.** 서버는 `safeMeetUrl` 로 못 쓰는 주소를 버리는데
        * (아이 화면이 눌러 새 창을 여는 값이라 마땅한 일이다), 화면은 「✅ 소식 N개를
        * 올렸어요」라고만 했다. 운영자는 넣었다고 믿고 다시 열었을 때 **빈 칸**을 본다 —
        * 에러도 없고 성공 메시지까지 떠서 무엇이 잘못됐는지 알 길이 없다.
        * 그래서 **치는 동안** 말해 준다. 확인 결과가 있으면 그게 먼저다(방금 누른 답이라).
        */}
      {(meetSay ?? meetUrlSay(defaultMeetUrl)) ? (
        <p className="notice-panel__meetsay" role="status">
          {meetSay ?? meetUrlSay(defaultMeetUrl)}
        </p>
      ) : null}
      {/**
        * 🎥 **어느 서비스를 쓸지 여기서 고른다**(2026-09-03 운영자 요청).
        * 목록·스펙·걸음은 shared 한 곳(`MEET_KINDS`) — 선생님 화면과 **같은 말**을 한다.
        * 🔴 인원·시간 한도를 여기서 못 보면 운영자는 **행사 당일에** 안다.
        */}
      <MeetPicker
        kind={meetPickKind}
        onKind={setPickedKind}
        onFill={(u) => {
          setDefaultMeetUrl(u);
          setPickedKind(null);
        }}
      />

      <label className="notice-panel__contact">
        🙋 문의·제보 폼 주소(https)
        <input
          type="url"
          value={contactUrl}
          onChange={(e) => setContactUrl(e.target.value)}
          placeholder="https://forms.gle/… (비우면 웹에 '곧 열려요'로 표시)"
        />
        <small>
          보호자가 문의를 보내는 외부 폼이에요. 개인정보는 폼(운영자 계정)에만 남고 우리 서버엔
          저장되지 않아요.
        </small>
      </label>

      <div className="notice-panel__actions">
        <button className="btn" onClick={add} disabled={!loaded}>+ 새 소식 쓰기</button>
      </div>

      <Msg text={msg} />

      {loaded && list.length === 0 ? (
        <Empty icon="📢">아직 소식이 없어요. <b>[+ 새 소식 쓰기]</b>를 눌러 시작하세요.</Empty>
      ) : null}

      <ul className="notice-panel__list">
        {list.map((n, i) => (
          <li key={n.id} className="notice-edit" data-pinned={n.pinned ? "1" : undefined}>
            <div className="notice-edit__row">
              <input
                className="notice-edit__title"
                value={n.title}
                placeholder="제목 (예: 체험판이 열렸어요!)"
                onChange={(e) => update(i, { title: e.target.value })}
              />
              <input
                type="date"
                value={toDateInput(n.date)}
                onChange={(e) => update(i, { date: fromDateInput(e.target.value) })}
              />
            </div>
            <textarea
              className="notice-edit__body"
              rows={3}
              value={n.body}
              placeholder="내용 (줄바꿈 그대로 보여요)"
              onChange={(e) => update(i, { body: e.target.value })}
            />
            <div className="notice-edit__row notice-edit__foot">
              <label className="notice-edit__pin">
                <input
                  type="checkbox"
                  checked={!!n.pinned}
                  onChange={(e) => update(i, { pinned: e.target.checked })}
                />
                📌 맨 위 고정
              </label>
              <button className="btn btn--sm" onClick={() => remove(i)}>
                삭제
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 안전 id([a-z0-9_]) 자동 생성 — 목록 안에서 중복되지 않게 숫자 접미. */
function nextId(list: NoticeDoc[], base: string): string {
  const used = new Set(list.map((n) => n.id));
  let i = 1;
  let id = `${base}_${i}`;
  while (used.has(id)) id = `${base}_${++i}`;
  return id;
}
function toDateInput(iso?: string): string {
  return kstDateInput(iso); // 🔴 기기 날짜로 풀면 한국보다 늦은 시간대 PC 에서 공지 날짜가 저장마다 하루씩 당겨졌다
}
/** 행사는 한국 기준 — KST 명시로 저장(기기 시간대 차이로 날짜가 밀리지 않게). */
function fromDateInput(v: string): string | undefined {
  return isoFromKstDate(v);
}
